import React, { PropsWithChildren, useState } from 'react';
import ReactPlayer from 'react-player';

import { useApolloClient } from '@apollo/client';
import { Modal } from '@opendoor/bricks/complex';
import { Button, Flex, Text } from '@opendoor/bricks/core';

import {
  OdProtosWebData_RecordedFeature,
  OdProtosWebData_VirtualAssessmentRecordingInput,
} from '../../__generated__/athenaGlobalTypes';
import { CheckIcon } from '../../components/icons/CheckIcon';
import { useSubmitSSVAByRecordedFeature } from '../../graphql/athena/mutations/SubmitSSVAByRecordedFeature';
import { useUpdateVirtualAssessmentRecordings } from '../../graphql/athena/mutations/UpdateVirtualAssessmentRecordings';
import { useVirtualAssessmentData } from '../../graphql/athena/queries/useVirtualAssessmentData';
import {
  FileUpload,
  generateS3Key,
  S3UploadDestination,
  uploadFileToS3,
} from './helpers/s3Helpers';
import { linkForUploadValidity } from './utils';
import { VideoUploadButton } from './VideoUploadButton';

export interface TaskProps {
  /** Title of the task */
  title: string;
  /** Subtitle of the task */
  subtitle: string;
  /** Whether the task is done */
  recordedFeature: OdProtosWebData_RecordedFeature;
}

interface VideoInteractionProps {
  task: TaskProps;
  sellerInputUUID: string;
}

export const VideoInteraction: React.FC<VideoInteractionProps & PropsWithChildren> = ({
  task,
  sellerInputUUID,
}) => {
  const apolloClient = useApolloClient();
  const [loading, setLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState(false);
  const [filesUploading, setFilesUploading] = useState<FileUpload[]>([]);
  const [showVideoModal, setShowVideoModal] = useState<boolean>(false);

  const { updateSSVARecordings, loading: isUpdateRecordingLoading } =
    useUpdateVirtualAssessmentRecordings(sellerInputUUID);
  const { submitSSVAByRecordedFeature, loading: isSubmitLoading } =
    useSubmitSSVAByRecordedFeature(sellerInputUUID);

  const { details, loading: vaLoading } = useVirtualAssessmentData(sellerInputUUID);
  if (vaLoading) return null;

  const isSubmissionCompleted = (feature: OdProtosWebData_RecordedFeature) => {
    if (feature == OdProtosWebData_RecordedFeature.INTERIOR) {
      return details?.latestSelfServeVirtualAssessment?.ssvaInteriorRecordingsSubmitted ?? false;
    }
    if (feature == OdProtosWebData_RecordedFeature.FRONTYARD) {
      return details?.latestSelfServeVirtualAssessment?.ssvaFrontyardRecordingsSubmitted ?? false;
    }
    if (feature == OdProtosWebData_RecordedFeature.BACKYARD) {
      return details?.latestSelfServeVirtualAssessment?.ssvaBackyardRecordingsSubmitted ?? false;
    }
    return false;
  };

  const updatingOrSubmittingSSVA = isUpdateRecordingLoading || isSubmitLoading;
  const uploadOrSubmitButtonDisabled =
    loading ||
    isUpdateRecordingLoading ||
    isSubmitLoading ||
    isSubmissionCompleted(task.recordedFeature);

  const viewButtonDisabled =
    loading ||
    !isSubmissionCompleted(task.recordedFeature) ||
    isUpdateRecordingLoading ||
    isSubmitLoading;

  const ssvaRecordings = details?.latestSelfServeVirtualAssessment?.ssvaRecordings ?? [];
  const uploadedFiles =
    ssvaRecordings?.filter(
      (recording: { recordedFeature: OdProtosWebData_RecordedFeature }) =>
        recording.recordedFeature === task.recordedFeature,
    ) ?? [];
  const filesExist = uploadedFiles.length > 0 || filesUploading.length > 0;
  const noFilesOrUploads = !filesExist && !loading && !isUpdateRecordingLoading;

  const videoLink = (uploadedFiles && uploadedFiles[0]?.preSignedRecordingUrl) || '';
  const { isValid: isValidLinkUpload } = linkForUploadValidity(videoLink);

  const onUploadingFileCompleted = async (
    uploads: FileUpload[],
    feature: OdProtosWebData_RecordedFeature,
  ) => {
    const newRecordings: Array<OdProtosWebData_VirtualAssessmentRecordingInput> =
      uploads.map((upload) => {
        return {
          fileName: upload.file.name,
          recordedFeature: feature,
          s3Directory: upload.s3Key,
        };
      }) ?? [];
    await updateSSVARecordings(newRecordings);
    setLoading(false);

    // Auto-submit SSVA if it is the first upload
    if (uploadedFiles.length === 0) {
      await submitSSVAByRecordedFeature(task.recordedFeature);
    }
  };
  // only allow uploading videos or images
  const isValidFileType = (file: File): boolean => {
    return file.type.startsWith('video') || file.type.startsWith('image');
  };

  /** onFileUploadUpdate is a callback function used to track s3 upload status */
  const onFileUploadUpdate = (upload: FileUpload) => {
    setFilesUploading((prevFilesUpload) => {
      const newFilesUpload = prevFilesUpload?.map((prevFileUpload) =>
        prevFileUpload.s3Key === upload.s3Key ? upload : prevFileUpload,
      );
      if (!newFilesUpload) {
        return [];
      }
      let hasErr = false;
      let allCompleted = true;

      // Check if all segments are completely uploaded
      for (const fileUpload of newFilesUpload) {
        if (!fileUpload.completed) {
          allCompleted = false;
        }

        if (fileUpload.errorOccurred) {
          hasErr = true;
        }
      }
      if (hasErr) {
        setLoading(false);
        setHasError(true);
        return [];
      }

      if (allCompleted && !isUpdateRecordingLoading) {
        onUploadingFileCompleted(newFilesUpload, task.recordedFeature);
      }
      return newFilesUpload;
    });
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.currentTarget.files) {
      let hasInvalidFileType = false;
      const newUploads: FileUpload[] = [];
      Array.from(e.currentTarget.files).forEach((file) => {
        hasInvalidFileType = hasInvalidFileType || !isValidFileType(file);
        // Only upload files with valid file type.
        if (isValidFileType(file)) {
          const fileUpload: FileUpload = {
            file: file,
            s3Key: generateS3Key(file, sellerInputUUID, S3UploadDestination.SSVA),
            progress: 0,
            uploadLatency: 0,
            completed: false,
            errorOccurred: false,
            emitMetrics: false,
            errorMessage: '',
          };
          uploadFileToS3(fileUpload, apolloClient, true, onFileUploadUpdate);
          newUploads.push(fileUpload);
        }
      });
      if (newUploads.length > 0) {
        setLoading(true);
        setHasError(false);
      }
      setFilesUploading(newUploads);
    }
  };

  const onCardClick = isValidLinkUpload
    ? () => window.open(videoLink, '_blank')
    : () => setShowVideoModal(true);

  return (
    <Flex alignItems={'center'} marginBottom={5} gap={2}>
      <div style={{ width: '22px' }}>{!viewButtonDisabled && <CheckIcon />}</div>
      <Text flex={1} fontWeight={'semibold'}>
        {task.title}:
        <Text flex={1} fontWeight={'normal'}>
          {task.subtitle}
        </Text>
      </Text>
      {noFilesOrUploads && (
        <VideoUploadButton
          id={`${task.recordedFeature}_add_another_video_input`}
          onChange={onFileChange}
          actionText={'Upload'}
          variant="link"
          loading={updatingOrSubmittingSSVA}
          disabled={uploadOrSubmitButtonDisabled}
          multiple={false}
          withPlusIcon={false}
          recordedFeature={task.recordedFeature}
        />
      )}
      {(loading || isSubmitLoading) && <Text>Uploading...</Text>}
      {!hasError && (
        <Button
          analyticsName="ssva-video-view-button"
          aria-label="video view button"
          onClick={onCardClick}
          size="sm"
          variant="secondary"
          width={'unset'}
          disabled={viewButtonDisabled}
        >
          View
        </Button>
      )}
      {!hasError && videoLink && (
        <Modal
          analyticsName="cake-ssva-video-upload-card-video-modal"
          className="video-modal"
          isOpen={showVideoModal}
          light={true}
          showClose
          onRequestClose={() => setShowVideoModal(false)}
          desktopAndTabletSize="sm"
          hasPadding={true}
          headerProps={{ title: 'Uploaded Video', titleProps: { as: 'h2' } }}
        >
          <ReactPlayer url={videoLink} playing={true} controls={true} width="100%" height="100%" />
        </Modal>
      )}
    </Flex>
  );
};
